import { LeadPersonDetailDto, LeadPersonDto, LeadStage } from "@growth/dto";
import { Button, cn, Input, PromptDialog } from "@growth/ui";
import {
  ArrowLeft,
  Copy,
  ExternalLink,
  LoaderCircle,
  PenLine,
  Reply,
  StickyNote,
  UserCheck,
  UserPlus,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ErrorText, PageHeader, selectClass } from "../../components/page-header";
import { useActive } from "../../lib/active";
import { apiErrorMessage } from "../../lib/api-error";
import { formatDate } from "../../lib/format";
import {
  useAddNote,
  useCreateDraft,
  useLeadPeople,
  useLeadPerson,
  useLogReply,
  useSendingSettings,
  useSetLinkedIn,
  useSetStage,
} from "../../lib/leads";
import {
  BEST_CHANNEL,
  BestChannel,
  card,
  ChannelRules,
  ContactStatusBadge,
  EngagementStrip,
  Panel,
  ROLE_LABEL,
  SampleBanner,
  stageLabel,
  STAGES,
  Timeline,
  when,
} from "./shared";

type Sort = "score" | "recent" | "name";

const recentOf = (person: LeadPersonDto) =>
  [person.lastEmailAt, person.lastVisitAt].filter(Boolean).sort().at(-1) ?? "";

export const PeoplePage = () => {
  const { brand } = useActive();
  const people = useLeadPeople();
  const [params, setParams] = useSearchParams();
  const channel = params.get("channel") ?? "all";
  const [stage, setStage] = useState<LeadStage | "all">("all");
  const [sort, setSort] = useState<Sort>("score");
  const [query, setQuery] = useState("");

  const rows = (people.data ?? [])
    .filter(
      (person) =>
        (channel === "all" || person.bestChannel === channel) &&
        (stage === "all" || person.stage === stage) &&
        `${person.name} ${person.accountName} ${person.title}`
          .toLowerCase()
          .includes(query.trim().toLowerCase()),
    )
    .sort((a, b) =>
      sort === "score"
        ? b.engagement.score - a.engagement.score || a.name.localeCompare(b.name)
        : sort === "recent"
          ? recentOf(b).localeCompare(recentOf(a))
          : a.name.localeCompare(b.name),
    );

  return (
    <section className="max-w-6xl space-y-6">
      <SampleBanner />
      <PageHeader eyebrow={`${brand?.name ?? ""} · Leads`} title="People">
        Everyone the agents found at approved accounts, with the contact route each one allows.
        Scores count only what people did, never mail scanners.
      </PageHeader>
      {people.isError && <ErrorText>{apiErrorMessage(people.error)}</ErrorText>}
      {people.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {people.data && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Best channel"
              className={cn(selectClass, "w-auto")}
              value={channel}
              onChange={(event) =>
                setParams(event.target.value === "all" ? {} : { channel: event.target.value }, {
                  replace: true,
                })
              }
            >
              <option value="all">Any channel</option>
              {Object.entries(BEST_CHANNEL).map(([id, { label }]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
            <select
              aria-label="Stage"
              className={cn(selectClass, "w-auto")}
              value={stage}
              onChange={(event) => setStage(event.target.value as LeadStage | "all")}
            >
              <option value="all">Any stage</option>
              {STAGES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Sort"
              className={cn(selectClass, "w-auto")}
              value={sort}
              onChange={(event) => setSort(event.target.value as Sort)}
            >
              <option value="score">Highest score</option>
              <option value="recent">Most recent activity</option>
              <option value="name">Name</option>
            </select>
            <Input
              aria-label="Search people"
              placeholder="Search"
              className="w-48"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          {rows.length === 0 ? (
            <p className={cn(card, "p-6 text-sm text-muted")}>
              {people.data.length
                ? "Nobody matches these filters."
                : "No people yet. They're found once accounts are approved."}
            </p>
          ) : (
            <div className={cn(card, "overflow-x-auto")}>
              <table className="w-full min-w-[60rem] text-left text-sm">
                <thead className="border-b border-line bg-paper/60 text-[11px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="px-4 py-2 font-semibold">Person</th>
                    <th className="px-3 py-2 font-semibold">Contact</th>
                    <th className="px-3 py-2 font-semibold">Stage</th>
                    <th className="px-3 py-2 text-right font-semibold">Score</th>
                    <th className="px-3 py-2 font-semibold">Last email</th>
                    <th className="px-3 py-2 font-semibold">Last visit</th>
                    <th className="px-3 py-2 font-semibold">LinkedIn</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((person) => (
                    <tr key={person.id} className="align-top hover:bg-paper/30">
                      <td className="px-4 py-3">
                        <Link
                          to={`/leads/people/${person.id}`}
                          className="font-medium text-ink hover:underline"
                        >
                          {person.name}
                        </Link>
                        <p className="text-xs text-muted">
                          {person.title}, {person.accountName}
                        </p>
                      </td>
                      <td className="space-y-1 px-3 py-3">
                        <ContactStatusBadge status={person.contactStatus} />
                        <p>
                          <BestChannel channel={person.bestChannel} />
                        </p>
                      </td>
                      <td className="px-3 py-3 text-xs text-ink">{stageLabel(person.stage)}</td>
                      <td className="px-3 py-3 text-right">
                        <p className="font-serif text-lg tabular-nums text-ink">
                          {person.engagement.score}
                        </p>
                        <EngagementStrip engagement={person.engagement} compact />
                      </td>
                      <td className="px-3 py-3 text-xs text-muted">{when(person.lastEmailAt)}</td>
                      <td className="px-3 py-3 text-xs text-muted">{when(person.lastVisitAt)}</td>
                      <td className="px-3 py-3 text-xs text-muted">
                        {person.linkedin.acceptedAt
                          ? "Accepted"
                          : person.linkedin.requestSentAt
                            ? "Request sent"
                            : person.linkedin.url
                              ? "Not yet"
                              : "No profile"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
};

// ─── The lead card ───────────────────────────────────────────────────────────────────────────

export const PersonPage = () => {
  const { id = "" } = useParams();
  const person = useLeadPerson(id);
  const settings = useSendingSettings();
  const data = person.data;

  return (
    <section className="max-w-6xl space-y-6">
      <SampleBanner />
      <Link
        to="/leads/people"
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> People
      </Link>
      {person.isError && <ErrorText>{apiErrorMessage(person.error)}</ErrorText>}
      {person.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {data && (
        <>
          <PersonHeader person={data} />
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="min-w-0 space-y-4">
              <Panel
                title="Engagement"
                about="Only what a person did counts. Opens from privacy proxies and clicks from mail scanners are kept but never score."
              >
                <EngagementStrip engagement={data.engagement} scoring={settings.data?.scoring} />
                <p className="text-xs text-muted">
                  Last email {when(data.lastEmailAt)} · last website visit {when(data.lastVisitAt)}
                </p>
              </Panel>
              <Panel title="Timeline">
                <Timeline events={data.events} scoring={settings.data?.scoring} />
              </Panel>
              {data.notes && (
                <Panel title="Notes">
                  <p className="whitespace-pre-line text-sm text-ink">{data.notes}</p>
                </Panel>
              )}
            </div>
            <aside className="space-y-4">
              <Contact person={data} />
              <LinkedInCard person={data} />
              <LeadFields person={data} />
            </aside>
          </div>
        </>
      )}
    </section>
  );
};

const PersonHeader = ({ person }: { person: LeadPersonDetailDto }) => {
  const { atLeast } = useActive();
  const navigate = useNavigate();
  const setStage = useSetStage();
  const create = useCreateDraft();
  const note = useAddNote();
  const reply = useLogReply();
  const [asking, setAsking] = useState<"note" | "reply" | null>(null);
  const canEmail = person.channels.some((rule) => rule.channel === "email" && rule.allowed);
  const error = setStage.error ?? create.error ?? note.error ?? reply.error;

  return (
    <header className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted">
            {ROLE_LABEL[person.role]} ·{" "}
            <Link to={`/leads/accounts/${person.accountId}`} className="hover:underline">
              {person.accountName}
            </Link>
          </p>
          <h1 className="font-serif text-3xl text-ink">{person.name}</h1>
          <p className="text-sm text-muted">{person.title}</p>
        </div>
        {atLeast("EDITOR") && (
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Stage"
              className={cn(selectClass, "w-auto")}
              value={person.stage}
              onChange={(event) =>
                setStage.mutate({ id: person.id, stage: event.target.value as LeadStage })
              }
            >
              {STAGES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            <Button
              disabled={!canEmail || create.isPending}
              title={canEmail ? undefined : "Email isn't allowed for this person; see Contact."}
              onClick={() =>
                create.mutate(person.id, { onSuccess: (id) => navigate(`/leads/outreach/${id}`) })
              }
            >
              <PenLine className="size-4" /> Write an email
            </Button>
            <Button variant="secondary" onClick={() => setAsking("reply")}>
              <Reply className="size-4" /> Log a reply
            </Button>
            <Button variant="ghost" onClick={() => setAsking("note")}>
              <StickyNote className="size-4" /> Note
            </Button>
          </div>
        )}
      </div>
      {error && <ErrorText>{apiErrorMessage(error)}</ErrorText>}
      <PromptDialog
        open={asking !== null}
        onOpenChange={(open) => !open && setAsking(null)}
        title={asking === "reply" ? `What did ${person.name.split(" ")[0]} say?` : "Add a note"}
        label={asking === "reply" ? "Their reply, or a summary of it" : "Note"}
        submitLabel="Save"
        onSubmit={(value) => {
          if (value.length < 2) return "Write a few words.";
          if (asking === "reply") reply.mutate({ id: person.id, text: value });
          else note.mutate({ id: person.id, text: value });
          return null;
        }}
      />
    </header>
  );
};

const Contact = ({ person }: { person: LeadPersonDetailDto }) => (
  <div className={cn(card, "space-y-3 p-4")}>
    <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">Contact</h2>
    <div className="space-y-1">
      <p className="break-all text-sm text-ink">{person.email ?? "No email"}</p>
      <ContactStatusBadge status={person.contactStatus} />
      {person.phone && <p className="text-sm text-ink">{person.phone}</p>}
    </div>
    <div className="rounded-md bg-paper/60 p-2 text-xs text-muted">
      Found on{" "}
      <a
        href={person.found.url}
        target="_blank"
        rel="noreferrer"
        className="break-all text-accent underline"
      >
        {person.found.url.replace(/^https?:\/\//, "")}
      </a>{" "}
      ({formatDate(person.found.seenAt)}): “{person.found.quote}”
    </div>
    <ChannelRules channels={person.channels} />
  </div>
);

const LinkedInCard = ({ person }: { person: LeadPersonDetailDto }) => {
  const { atLeast } = useActive();
  const set = useSetLinkedIn();
  const { brand, me } = useActive();
  const [copied, setCopied] = useState(false);
  const allowed = person.channels.some((rule) => rule.channel === "linkedin" && rule.allowed);
  const sender = me?.user.name?.split(" ")[0] ?? "I";
  const noteText = `Hi ${person.name.split(" ")[0]}, I'm ${sender} from ${brand?.name ?? "our team"}. I came across your work at ${person.accountName} and would like to connect.`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(noteText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className={cn(card, "space-y-3 p-4")}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
          LinkedIn, by hand
        </h2>
        {person.linkedin.url && (
          <a
            href={person.linkedin.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-accent underline"
          >
            Open profile <ExternalLink className="size-3" />
          </a>
        )}
      </div>
      {!person.linkedin.url ? (
        <p className="text-sm text-muted">No profile found yet.</p>
      ) : (
        <>
          <ul className="space-y-2 text-sm">
            {(
              [
                {
                  field: "sent",
                  label: "Request sent",
                  icon: UserPlus,
                  at: person.linkedin.requestSentAt,
                },
                {
                  field: "accepted",
                  label: "Request accepted",
                  icon: UserCheck,
                  at: person.linkedin.acceptedAt,
                },
              ] as const
            ).map(({ field, label, icon: Icon, at }) => (
              <li key={field}>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={Boolean(at)}
                    disabled={!allowed || !atLeast("EDITOR") || set.isPending}
                    onChange={(event) =>
                      set.mutate({ id: person.id, field, on: event.target.checked })
                    }
                  />
                  <Icon className="size-4 text-muted" />
                  {label}
                  <span className="ml-auto text-xs text-muted">{at ? formatDate(at) : ""}</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="space-y-1.5 rounded-md bg-paper/60 p-2">
            <p className="text-xs text-ink">{noteText}</p>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted">{noteText.length} / 300 characters</span>
              <button
                type="button"
                onClick={copy}
                className="inline-flex items-center gap-1 text-xs text-accent underline"
              >
                <Copy className="size-3" /> {copied ? "Copied" : "Copy the note"}
              </button>
            </div>
          </div>
          <p className="text-[11px] leading-relaxed text-muted">
            Send it yourself from your own account, then tick the box. Agents never log in to or
            automate LinkedIn.
          </p>
        </>
      )}
    </div>
  );
};

/** Michael's lead fields, as they would sync to a CRM. */
const LeadFields = ({ person }: { person: LeadPersonDetailDto }) => {
  const [first, ...rest] = person.name.split(" ");
  const rows: [string, string][] = [
    ["First name", first ?? ""],
    ["Last name", rest.join(" ")],
    ["Email", person.email ?? "–"],
    ["Company", person.accountName],
    ["LinkedIn profile", person.linkedin.url ? "Yes" : "–"],
    [
      "LinkedIn request sent",
      person.linkedin.requestSentAt ? formatDate(person.linkedin.requestSentAt) : "–",
    ],
    [
      "LinkedIn request accepted",
      person.linkedin.acceptedAt ? formatDate(person.linkedin.acceptedAt) : "–",
    ],
    ["Date of last email", person.lastEmailAt ? formatDate(person.lastEmailAt) : "–"],
    ["Lead score", String(person.engagement.score)],
    ["Last website visit", person.lastVisitAt ? formatDate(person.lastVisitAt) : "–"],
    ["Lead owner", person.owner],
    ["Lead source", person.leadSource],
    ["Lead status", stageLabel(person.stage) ?? ""],
  ];
  return (
    <details className={cn(card, "p-4")}>
      <summary className="cursor-pointer text-xs font-semibold uppercase tracking-widest text-muted">
        Lead fields (CRM view)
      </summary>
      <dl className="mt-3 grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted">{label}</dt>
            <dd className="break-all text-ink">{value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
};
